<template>
  <FreeBottomSheet :name="name" @onOpen="actualizeState">
    <template #header="{ hide }">
      <FreeHeader class="p-4">
        <h2
          class="text-text-neutral-primary text-base font-medium"
        >
          {{ t('main.cabinClass.select') }}
        </h2>
        <template #rightAction>
          <FreeCloseButton
            @click="hide"
          />
        </template>
      </FreeHeader>
    </template>
    <template #default="{ hide }">
      <div class="flex min-h-[67vh] flex-col space-y-6 px-4 pb-4">
        <PassengersClassesTab
          :active-tab="cabinsDraft.state.value.cabinClass"
          :is-youth="cabinsDraft.state.value.isYouth"
          :source="source"
          @onTabChange="updateCabinClass"
        />
        <div
          class="relative flex flex-grow flex-col items-center justify-between pb-4"
        >
          <SelectPassengers
            class="w-full"
            :is-youth-tab="cabinsDraft.state.value.isYouth"
            :passengers="passengersCounter.state.value"
            :source="source"
            @onPassengersChange="passengersCounter.updatePassengers"
            @onChangeError="showAlert"
          />
          <FreeAlert
            ref="alertRef"
            class="bottom-4 w-[90%]"
          >
            <template #icon>
              <img
                src="@/assets/images/red-warning.svg"
                width="24"
                height="24"
                alt=""
              />
            </template>
            <template #default="{ content }">
              <p class="text-text-neutral-primary">{{ content }}</p>
            </template>
          </FreeAlert>
        </div>
        <FreeButton
          class="green-main-button w-full"
          @click="
            commitState();
            hide();
          "
        >
          {{ t('main.cabinClass.add') }}
        </FreeButton>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import {
    FreeBottomSheet,
    FreeButton,
    FreeCloseButton,
    FreeHeader,
    FreeAlert,
  } from 'freedom-shared';
  import { passengersCounterModel, useDraftState, searchForm } from 'shared';
  import { useI18n } from 'vue-i18n';
  import type { Passengers, CabinClass } from 'shared';

  import { PassengersClassesTab } from '@/features/passengers-classes-tab';
  import { SelectPassengers } from '@/features/select-passengers';

  const props = defineProps<{
    name: string;
    cabinClass: CabinClass;
    passengers: Passengers;
    isYouth: boolean;
    source: string;
  }>();

  const emit = defineEmits<{
    (e: 'onSetIsYouth', value: boolean): void;
    (e: 'onCabinClassChange', value: CabinClass): void;
    (e: 'onPassengersStateChange', value: Passengers): void;
  }>();

  const { t } = useI18n();
  const alertRef = ref();

  const cabinsDraft = useDraftState({
    initialDraft: {
      isYouth: props.isYouth,
      cabinClass: props.cabinClass,
    },
  });

  const passengersCounter = passengersCounterModel.usePassengersCounter({
    initialPassengers: searchForm.config.DEFAULT_PASSENGERS_STATE,
  });

  const actualizeState = () => {
    cabinsDraft.updateDraft({
      isYouth: props.isYouth,
      cabinClass: props.cabinClass,
    });
    passengersCounter.updatePassengers(props.passengers);
  };

  const updateCabinClass = (cabinClass: string, isYouth: boolean) => {
    cabinsDraft.updateDraft({ cabinClass, isYouth });
  };

  const commitState = () => {
    emit('onSetIsYouth', cabinsDraft.state.value.isYouth);
    emit('onCabinClassChange', cabinsDraft.state.value.cabinClass);
    emit('onPassengersStateChange', passengersCounter.passengersList.value);
  };

  const showAlert = (payload: { [index: string]: string }) => {
    const { adt, inf } = passengersCounter.passengersList.value;
    const warningText = t(searchForm.lib.getAlertTextKey(payload, adt, inf));

    if (warningText) {
      alertRef.value.show(warningText);
    }
  };
</script>
